// src/components/common/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import { FaLinkedin, FaInstagram } from 'react-icons/fa';

const EXPLORE = [
  { to: '/',        label: 'Home' },
  { to: '/explore', label: 'Purchase' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/contact', label: 'Contact' },
];

const POLICIES = [
  { to: '/privacy-policy',   label: 'Privacy Policy' },
  { to: '/cookie-policy',    label: 'Cookie Policy' },
  { to: '/terms-of-service', label: 'Terms of Service' },
  { to: '/return-policy',    label: 'Return Policy' },
  { to: '/disclaimer',       label: 'Disclaimer' },
];

const SOCIALS = [
  { href: 'https://linkedin.com/company/crazed-entertainment/', Icon: FaLinkedin, label: 'LinkedIn' },
  { href: 'https://instagram.com/akture.video',                  Icon: FaInstagram, label: 'Instagram' },
];

const FootLink = ({ to, children }) => (
  <li>
    <Link to={to} className="text-gray-400 hover:text-white text-sm transition-colors duration-150">
      {children}
    </Link>
  </li>
);

const ColHeading = ({ children }) => (
  <h4 className="text-xs font-black uppercase tracking-[0.16em] text-gray-500 mb-4">{children}</h4>
);

const Footer = () => (
  <footer className="bg-gray-950 text-white">

    {/* ── Main grid ──────────────────────────────────────────────────────── */}
    <div className="max-w-screen-xl mx-auto px-6 lg:px-8 pt-16 pb-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">

      {/* Brand column */}
      <div className="sm:col-span-2 lg:col-span-1">
        {/* Logo mark — use the SVG with a filter to make it look good on dark */}
        <div className="flex items-center gap-3 mb-4">
          <img
            src="/1.svg"
            alt="Akture"
            className="h-10 w-auto object-contain"
            style={{ filter: 'brightness(0) saturate(100%) invert(18%) sepia(97%) saturate(3000%) hue-rotate(343deg) brightness(90%)' }}
            draggable={false}
          />
          <div>
            <p className="text-lg font-black leading-none text-white tracking-tight">Akture</p>
            <p className="text-[11px] text-gray-500 mt-0.5 font-medium tracking-wide">Action Capture System</p>
          </div>
        </div>
        <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
          HD sports footage from courts near you. Scan, purchase, and relive your best moments.
        </p>

        {/* Social icons */}
        <div className="flex items-center gap-3 mt-6">
          {SOCIALS.map(({ href, Icon, label }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
              className="w-9 h-9 rounded-lg bg-white/5 border border-white/8 flex items-center justify-center text-gray-400 hover:bg-brand-red hover:border-brand-red hover:text-white transition-all duration-200">
              <Icon className="text-base"/>
            </a>
          ))}
        </div>
      </div>

      {/* Explore */}
      <div>
        <ColHeading>Explore</ColHeading>
        <ul className="space-y-2.5">
          {EXPLORE.map(({ to, label }) => <FootLink key={label} to={to}>{label}</FootLink>)}
        </ul>
      </div>

      {/* Policies */}
      <div>
        <ColHeading>Policies</ColHeading>
        <ul className="space-y-2.5">
          {POLICIES.map(({ to, label }) => <FootLink key={label} to={to}>{label}</FootLink>)}
        </ul>
      </div>

      {/* Newsletter / CTA */}
      <div>
        <ColHeading>Stay Updated</ColHeading>
        <p className="text-sm text-gray-400 mb-4 leading-relaxed">
          New courts, new features, new drops — be the first to know.
        </p>
        <Link to="/register"
          className="inline-flex items-center px-5 py-2.5 bg-brand-red text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-sm">
          Create Free Account
        </Link>
      </div>
    </div>

    {/* ── Bottom bar ─────────────────────────────────────────────────────── */}
    <div className="border-t border-white/6">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-gray-600">
        <span>&copy; {new Date().getFullYear()} Akture. All rights reserved.</span>
        <div className="flex items-center gap-4">
          <Link to="/privacy-policy"   className="hover:text-gray-400 transition-colors">Privacy</Link>
          <Link to="/terms-of-service" className="hover:text-gray-400 transition-colors">Terms</Link>
          <Link to="/contact"          className="hover:text-gray-400 transition-colors">Contact</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
